import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import {
  FineDetailsPublicResponse,
  PaymentSetupPublicResponse,
  postFinesPayment,
} from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { Dispatch, SetStateAction, useEffect, useRef } from "react"
import { sharedColors } from "ui/theme/colors"
import { busPaymentReportStep } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { ErrorType } from "../Payment"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-bottom: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
    padding-bottom: 48px;
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
    flex-direction: row;
  }
`

const TextDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const DetailsBox = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 32px;
  border-radius: 12px;
  border: 1px solid ${sharedColors.borderGray};
  background-color: white;
  box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 24px;
    gap: 40px;
  }
`

const TopBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
    padding-bottom: 40px;
    border-bottom: 1px solid ${sharedColors.borderGray};
  }
`

const ColumnRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 24px;
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 24px;
  }
`
const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`

const DetailDesktop = styled.div`
  display: none;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 260px;
  }
`

const MobileRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledTypography = styled(Typography)`
  /* ---- Cap-height trim ---- */
  text-box-trim: trim-both; /* remove space above & below */
  text-box-edge: cap alphabetic; /* top to cap line, bottom to baseline */
`

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear().toString().slice(-2)
  return `${day}/${month}/${year}`
}

export default function SecondStage({
  setIsLoading,
  toThirdStage,
  data,
  setPaymentData,
  stepData,
}: {
  setIsLoading: Dispatch<SetStateAction<boolean>>
  toThirdStage: () => void
  data: FineDetailsPublicResponse
  setPaymentData: Dispatch<
    SetStateAction<PaymentSetupPublicResponse | undefined>
  >
  setError: Dispatch<SetStateAction<ErrorType | undefined>>
  stepData: {
    icon: string
    title: string
    subTitle: string
    stepName: string
  }
}) {
  const tInner = useTranslations("Forms")
  const textDivRef = useRef<HTMLDivElement>(null)

  // Focus management for accessibility - focus on component mount
  useEffect(() => {
    if (textDivRef.current) {
      textDivRef.current.focus()
    }
  }, [])

  const { mutate } = useMutation({
    mutationFn: postFinesPayment,
    onMutate: () => {
      setIsLoading(true)
    },
    onSuccess: (res) => {
      const data = res.data.body
      if (data) {
        setPaymentData(data)
        setIsLoading(false)
        toThirdStage()
      } else {
        setIsLoading(false)
      }
    },
    onError: () => {
      setIsLoading(false)
    },
  })
  const handlePayment = () => {
    busPaymentReportStep(
      tInner("pay"),
      "2", // Step number
      stepData.stepName, // Step name
    )
    mutate({
      fineId: data.fineId,
      fineNumerator: data.fineNumerator,
      totalNis: data.fineAmount,
    })
  }

  return (
    <StyledLayout>
      <TextBox>
        <Image
          src={getUmbracoMediaUrl(stepData?.icon)}
          width={80}
          height={80}
          alt="icon"
        />
        <TextDiv ref={textDivRef} tabIndex={0}>
          <Typography variant="h3">{stepData?.title}</Typography>
          <Typography variant="body1">{stepData?.subTitle}</Typography>
        </TextDiv>
      </TextBox>
      <DetailsBox>
        <Typography variant="h4" component={"h3"}>
          {tInner("details")}
        </Typography>
        <TopBox>
          <ColumnRow>
            <Detail>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("fullName")}
              </StyledTypography>
              <StyledTypography variant="body1">
                {data.firstName} {data.lastName}
              </StyledTypography>
            </Detail>
            <Detail>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("reportNumber")}
              </StyledTypography>
              <StyledTypography variant="body1">{data.fineId}</StyledTypography>
            </Detail>
            <Detail>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("formNo")}{" "}
              </StyledTypography>
              <StyledTypography variant="body1">
                {data.fineNumerator}
              </StyledTypography>
            </Detail>
            <MobileRow>
              <Detail>
                <StyledTypography variant="body1" fontWeight={700}>
                  {tInner("eventTime")}
                </StyledTypography>
                <StyledTypography variant="body1">
                  {data.eventHour}
                </StyledTypography>
              </Detail>
              <Detail>
                <StyledTypography variant="body1" fontWeight={700}>
                  {tInner("eventDate")}
                </StyledTypography>
                <StyledTypography variant="body1">
                  {formatDate(data.eventDate)}
                </StyledTypography>
              </Detail>
            </MobileRow>

            <DetailDesktop>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("eventTime")}
              </StyledTypography>
              <StyledTypography variant="body1">
                {data.eventHour}
              </StyledTypography>
            </DetailDesktop>
            <DetailDesktop>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("eventDate")}
              </StyledTypography>
              <StyledTypography variant="body1">
                {formatDate(data.eventDate)}
              </StyledTypography>
            </DetailDesktop>
          </ColumnRow>
        </TopBox>
        <ColumnRow>
          <MobileRow>
            <Detail>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("lineNumber")}
              </StyledTypography>
              <StyledTypography variant="body1">
                {data.lineNumber}
              </StyledTypography>
            </Detail>
            <Detail>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("eventLocation")}
              </StyledTypography>
              <StyledTypography variant="body1">
                {data.eventPlace}
              </StyledTypography>
            </Detail>
          </MobileRow>
          <DetailDesktop>
            <StyledTypography variant="body1" fontWeight={700}>
              {tInner("lineNumber")}
            </StyledTypography>
            <StyledTypography variant="body1">
              {data.lineNumber}
            </StyledTypography>
          </DetailDesktop>
          <DetailDesktop>
            <StyledTypography variant="body1" fontWeight={700}>
              {tInner("eventLocation")}
            </StyledTypography>
            <StyledTypography variant="body1">
              {data.eventPlace}
            </StyledTypography>
          </DetailDesktop>
          <DetailDesktop />
          <ButtonContainer>
            <Detail>
              <StyledTypography variant="body1" fontWeight={700}>
                {tInner("amountToBeCharged")}
              </StyledTypography>
              <StyledTypography variant="number" color={sharedColors.brandDark}>
                ₪{data.fineAmount}
              </StyledTypography>
            </Detail>
            <Button variant="primary" fullWidth onClick={handlePayment}>
              {tInner("pay")}
            </Button>
          </ButtonContainer>
        </ColumnRow>
      </DetailsBox>
    </StyledLayout>
  )
}
